// src/components/MoreRTLDemos.jsx
import React, { useRef } from "react";

/* ------- Charcoal background with blended top fade + animated dots ------- */
function ShowcaseSection({ children }) {
  // Parallax (outer) wrappers; CSS drift runs on inner wrapper
  const leftP = useRef(null);
  const rightP = useRef(null);

  const reduceMotion =
    typeof window !== "undefined" &&
    window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  const onMove = (e) => {
    if (reduceMotion) return;
    const r = e.currentTarget.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width - 0.5; // -0.5..0.5
    const py = (e.clientY - r.top) / r.height - 0.5;
    const tx = px * 12; // subtle parallax
    const ty = py * 8;
    if (leftP.current)
      leftP.current.style.transform = `translate3d(${tx}px, ${ty}px, 0)`;
    if (rightP.current)
      rightP.current.style.transform = `translate3d(${-tx}px, ${-ty}px, 0)`;
  };
  const onLeave = () => {
    if (leftP.current) leftP.current.style.transform = "translate3d(0,0,0)";
    if (rightP.current) rightP.current.style.transform = "translate3d(0,0,0)";
  };

  return (
    <section
      className="relative isolate overflow-hidden"
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      aria-label="More mass tort demos"
    >
      {/* CHARCOAL BASE + SOFT TOP FADE (no band) */}
      <div className="absolute inset-0 -z-10">
        {/* Charcoal gradient base */}
        <div
          className="absolute inset-0"
          style={{
            background: "linear-gradient(180deg, #0F1520 0%, #0B101A 100%)",
          }}
        />
        {/* Gentle gold-ish depth glow (very subtle) */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(900px 480px at 50% 68%, rgba(200,155,60,0.08) 0%, rgba(200,155,60,0.02) 60%, rgba(0,0,0,0) 100%)",
          }}
        />
        {/* Soft top lightening fade to separate from navbar */}
        <div
          className="absolute inset-x-0 top-0 h-[45%] pointer-events-none"
          style={{
            background:
              "linear-gradient(to bottom, rgba(255,255,255,0.10) 0%, rgba(255,255,255,0.06) 40%, rgba(255,255,255,0.03) 70%, rgba(255,255,255,0) 100%)",
          }}
        />
        {/* Tiny noise so dark gradients don't band */}
        <div
          className="absolute inset-0 opacity-[0.03] mix-blend-overlay pointer-events-none"
          style={{
            backgroundImage:
              "url('data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%2248%22 height=%2248%22 viewBox=%220 0 48 48%22><filter id=%22n%22><feTurbulence type=%22fractalNoise%22 baseFrequency=%220.95%22 numOctaves=%222%22 stitchTiles=%22stitch%22/></filter><rect width=%2248%22 height=%2248%22 filter=%22url(%23n)%22 opacity=%220.35%22/></svg>')",
          }}
        />
      </div>

      {/* DOTTED RECTANGLES — Continuous drift (18–24s) + small parallax */}
      <div ref={leftP} className="dot-parallax left">
        <div className="dot-drift slow">
          <div className="dot-grid left" />
        </div>
      </div>
      <div ref={rightP} className="dot-parallax right">
        <div className="dot-drift slower reverse">
          <div className="dot-grid right" />
        </div>
      </div>

      <div className="relative">{children}</div>

      <style>{`
        .dot-parallax{
          position:absolute;
          pointer-events:none;
          will-change: transform;
          transition: transform 160ms ease-out;
        }
        .dot-parallax.left{ left: 26px; top: 34px; }
        .dot-parallax.right{ right: 26px; bottom: 34px; }

        /* Continuous drift — inner wrapper so it combines with parallax */
        .dot-drift{ animation: floatXY 22s ease-in-out infinite alternate; }
        .dot-drift.slow   { animation-duration: 20s; }     /* ~18–22s */
        .dot-drift.slower { animation-duration: 24s; }     /* ~24s     */
        .dot-drift.reverse{ animation-direction: alternate-reverse; }

        /* Smaller, classy blocks */
        .dot-grid{
          background-size: 12px 12px;
          border-radius: 8px;
          filter: drop-shadow(0 1px 0 rgba(0,0,0,.25));
        }
        .dot-grid.left{
          width: 116px;           /* was large; now compact */
          height: 156px;
          background-image: radial-gradient(rgba(200,155,60,0.60) 1.2px, transparent 1.2px);
        }
        .dot-grid.right{
          width: 116px;
          height: 156px;
          background-image: radial-gradient(rgba(200,155,60,0.55) 1.15px, transparent 1.15px);
        }

        @keyframes floatXY {
          0%   { transform: translate3d(0,0,0); }
          50%  { transform: translate3d(10px, -7px, 0); }
          100% { transform: translate3d(-10px, 6px, 0); }
        }

        @media (prefers-reduced-motion: reduce){
          .dot-parallax{ transition: none; }
          .dot-drift{ animation: none; }
        }
      `}</style>
    </section>
  );
}

/* -------- Content (unchanged), headline pulled up remains -------- */
export default function MoreRTLDemos() {
  return (
    <ShowcaseSection>
      {/* slightly reduced top padding + negative margin to lift heading */}
      <div className="relative z-0 overflow-hidden pt-28 pb-40 md:pt-28 md:pb-44">
        <div className="relative z-10 max-w-5xl mx-auto px-4 text-center -mt-6 md:-mt-12">
          <h2 className="text-3xl md:text-5xl font-extrabold uppercase tracking-wide leading-tight">
            <span className="bg-gradient-to-r from-sky-400 via-fuchsia-500 to-pink-500 bg-clip-text text-transparent">
              Discover More Mass Tort
            </span>
            <br className="hidden md:block" />
            <span className="bg-gradient-to-r from-sky-400 via-fuchsia-500 to-pink-500 bg-clip-text text-transparent">
              Demos
            </span>
          </h2>
          {/* preview card */}+{" "}
          <div className="mx-auto mt-6 md:mt-8 w-[420px] md:w-[520px] max-w-full group">
            <div className="rounded-3xl bg-white p-1 border border-gray-200 shadow-[0_18px_50px_rgba(0,0,0,0.35)] transition-all duration-300 group-hover:-translate-y-2 group-hover:border-[#C89B3C] group-hover:border-[3px] group-hover:ring-2 group-hover:ring-[#C89B3C]/35">
              <div className="rounded-2xl overflow-hidden ring-1 ring-black/10 h-56 md:h-72">
                <img
                  src="/tort/demortl.jpg"
                  alt="Mass tort website preview"
                  className="block w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                  loading="lazy"
                />
              </div>
            </div>
            <div className="mt-3 text-white/90 font-semibold text-sm md:text-base">
              MassTort Demo –
            </div>
          </div>
          {/* Option B copy block (unchanged) */}
          <div className="mx-auto mt-10 md:mt-12 max-w-3xl text-left">
            <h3 className="text-lg md:text-xl font-semibold text-white/95">
              Why people choose a mass tort
            </h3>
            <p className="mt-2 text-slate-200/90 leading-relaxed">
              When many people are hurt by the same product or exposure,
              mass-tort litigation lets you{" "}
              <span className="font-semibold">keep control of your case</span>{" "}
              while benefiting from a coordinated strategy and shared evidence.
            </p>

            <ul className="mt-4 space-y-2 text-slate-200/90">
              <li className="flex items-start gap-3">
                <span className="mt-1 h-2 w-2 rounded-full bg-[#C89B3C]/85" />
                <span>
                  Your injuries and losses are valued{" "}
                  <span className="font-medium">individually</span>
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="mt-1 h-2 w-2 rounded-full bg-[#C89B3C]/85" />
                <span>Uses national experts and consolidated discovery</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="mt-1 h-2 w-2 rounded-full bg-[#C89B3C]/85" />
                <span>Helps surface internal company documents faster</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="mt-1 h-2 w-2 rounded-full bg-[#C89B3C]/85" />
                <span>
                  You owe <span className="font-medium">no fees</span> unless
                  there’s a recovery
                </span>
              </li>
            </ul>

            <p className="mt-4 text-slate-300/90 text-sm md:text-base">
              <em>Curious if you qualify?</em> The quick questions above help us
              understand your situation.
            </p>
          </div>
        </div>
      </div>
    </ShowcaseSection>
  );
}
